import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Message as MessageIcon,
  Send as SendIcon,
  Person as PersonIcon,
  SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { Conversation, Message, Customer } from '../types';
import { MagicBackground } from '../components/MagicBackground';
import { format } from 'date-fns';

export default function Messages() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [customers, setCustomers] = useState<Record<string, Customer>>({});
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterChannel, setFilterChannel] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadConversations();
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      loadConversationMessages(selectedConversation.conversationId);
    }
  }, [selectedConversation]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterChannel !== 'all') params.channel = filterChannel;
      const data = await apiService.getConversations(params);
      setConversations(data);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const customerList = await apiService.getCustomers();
      const customerMap: Record<string, Customer> = {};
      customerList.forEach(c => {
        customerMap[c.customerId] = c;
      });
      setCustomers(customerMap);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadConversationMessages = async (conversationId: string) => {
    try {
      setMessagesLoading(true);
      const data = await apiService.getConversationMessages(conversationId);
      setMessages(data.messages);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedConversation || !newMessage.trim()) return;

    try {
      await apiService.sendMessage({
        customerId: selectedConversation.customerId,
        content: newMessage,
        channel: selectedConversation.channel,
        sender: 'human',
      });
      setNewMessage('');
      // Reload messages
      await loadConversationMessages(selectedConversation.conversationId);
      // Reload conversations to update last message
      await loadConversations();
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const getCustomerName = (customerId: string): string => {
    const customer = customers[customerId];
    return customer ? customer.name : 'Unknown Customer';
  };

  const filteredConversations = conversations.filter(conv => {
    if (searchTerm) {
      const customerName = getCustomerName(conv.customerId).toLowerCase();
      const lastMessage = conv.lastMessage?.content?.toLowerCase() || '';
      return customerName.includes(searchTerm.toLowerCase()) || lastMessage.includes(searchTerm.toLowerCase());
    }
    return true;
  });

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
        }}
      >
        <MagicBackground />
        <CircularProgress sx={{ color: '#667eea', position: 'relative', zIndex: 1 }} size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
      minHeight: '100vh', 
      position: 'relative',
      py: 4 
    }}>
      <MagicBackground />
      
      <Box maxWidth="xl" sx={{ mx: 'auto', px: 3, position: 'relative', zIndex: 1 }}>
        <Box mb={4}>
          <Typography 
            variant="h3" 
            fontWeight="bold" 
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Messages & Conversations
          </Typography>
        </Box>

        <Box display="flex" gap={3} sx={{ height: 'calc(100vh - 200px)' }}>
          {/* Conversations List */}
          <Card
            sx={{
              width: 400,
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent sx={{ pb: 2 }}>
              <Box display="flex" gap={2} mb={2}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search conversations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  }}
                />
              </Box>
              <Box display="flex" gap={1} mb={2}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    onChange={(e) => {
                      setFilterStatus(e.target.value);
                      loadConversations();
                    }}
                    label="Status"
                    sx={{
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Channel</InputLabel>
                  <Select
                    value={filterChannel}
                    onChange={(e) => {
                      setFilterChannel(e.target.value);
                      loadConversations();
                    }}
                    label="Channel"
                    sx={{
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="sms">SMS</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="whatsapp">WhatsApp</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </CardContent>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {filteredConversations.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="rgba(255,255,255,0.7)">
                    No conversations found
                  </Typography>
                </Box>
              ) : (
                <List>
                  {filteredConversations.map((conv) => (
                    <ListItem
                      key={conv.conversationId}
                      button
                      onClick={() => setSelectedConversation(conv)}
                      selected={selectedConversation?.conversationId === conv.conversationId}
                      sx={{
                        '&.Mui-selected': {
                          bgcolor: 'rgba(102, 126, 234, 0.2)',
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#667eea' }}>
                          <PersonIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={getCustomerName(conv.customerId)}
                        secondary={
                          <Box>
                            <Typography variant="caption" color="rgba(255,255,255,0.7)" display="block">
                              {conv.lastMessage?.content || 'No messages yet'}
                            </Typography>
                            <Box display="flex" gap={1} mt={0.5}>
                              <Chip
                                label={conv.channel.toUpperCase()}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  bgcolor: 'rgba(102, 126, 234, 0.2)',
                                  color: '#667eea',
                                }}
                              />
                              <Typography variant="caption" color="rgba(255,255,255,0.5)">
                                {format(new Date(conv.lastMessageAt), 'MMM d, h:mm a')}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        primaryTypographyProps={{
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Card>

          {/* Message Thread */}
          <Card
            sx={{
              flex: 1,
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {selectedConversation ? (
              <>
                <CardContent sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" color="white" fontWeight={600}>
                        {getCustomerName(selectedConversation.customerId)}
                      </Typography>
                      <Chip
                        label={selectedConversation.channel.toUpperCase()}
                        size="small"
                        sx={{
                          mt: 0.5,
                          bgcolor: 'rgba(102, 126, 234, 0.2)',
                          color: '#667eea',
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                  {messagesLoading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                      <CircularProgress />
                    </Box>
                  ) : messages.length === 0 ? (
                    <Box textAlign="center" p={4}>
                      <Typography variant="body2" color="rgba(255,255,255,0.7)">
                        No messages yet
                      </Typography>
                    </Box>
                  ) : (
                    <Box display="flex" flexDirection="column" gap={2}>
                      {messages.map((msg) => (
                        <Box
                          key={msg.messageId}
                          sx={{
                            display: 'flex',
                            justifyContent: msg.direction === 'inbound' ? 'flex-start' : 'flex-end',
                          }}
                        >
                          <Paper
                            sx={{
                              p: 2,
                              maxWidth: '70%',
                              bgcolor: msg.direction === 'inbound'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(102, 126, 234, 0.3)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              {msg.sender === 'ai-agent' && (
                                <SmartToyIcon sx={{ fontSize: 16, color: '#667eea' }} />
                              )}
                              <Typography variant="caption" color="rgba(255,255,255,0.7)">
                                {msg.sender === 'ai-agent' ? 'AI Agent' : msg.sender === 'customer' ? 'Customer' : 'You'}
                              </Typography>
                              <Typography variant="caption" color="rgba(255,255,255,0.5)" ml="auto">
                                {format(new Date(msg.timestamp), 'h:mm a')}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="white">
                              {msg.content}
                            </Typography>
                          </Paper>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <Box display="flex" gap={1}>
                    <TextField
                      fullWidth
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                        },
                      }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                      sx={{
                        bgcolor: '#667eea',
                        '&:hover': { bgcolor: '#5568d3' },
                      }}
                    >
                      <SendIcon />
                    </Button>
                  </Box>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <MessageIcon sx={{ fontSize: 64, color: 'rgba(255,255,255,0.3)' }} />
                <Typography variant="h6" color="rgba(255,255,255,0.7)">
                  Select a conversation to view messages
                </Typography>
              </Box>
            )}
          </Card>
        </Box>
      </Box>
    </Box>
  );
}
